import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Eye, 
  CheckCircle, 
  Package, 
  Check, 
  X, 
  ShoppingBag, 
  Truck,
  User,
  IndianRupee
} from "lucide-react";
import { useAdminOrders } from "../../hooks/useAdminOrder";
import { OrderService } from "../../services/orderService";
import { formatDate } from "../../utils/date";
import { formatPrice } from "../../utils/utility";
import { Order, OrderStatus } from "../../types/order";
import ShippingDetailsModal from "../admin/ShippingDetailsModal";
import useToastStore from "../../store/useToastStore";

interface OrderListProps {
  status: string;
}

const OrderList = ({ status }: OrderListProps) => {
  const navigate = useNavigate();
  const { showToast } = useToastStore();
  
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<() => void>(() => {});
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");

  // Use the same hook for all statuses, including 'recent'
  const { data: orders, isLoading, error, refetch } = useAdminOrders(status);

  const handleStatusUpdate = async (orderId: string, newStatus: OrderStatus, shippingDetails?: any) => {
    try {
      await OrderService.updateOrderStatus(orderId, newStatus, shippingDetails);
      showToast('Order status updated successfully', 'success');
      refetch(); // Refresh the orders list
    } catch (error) {
      showToast('Failed to update order status', 'error');
      console.error('Error updating order status:', error);
    }
  };

  const handleConfirmAction = (action: () => void, message: string) => {
    setConfirmationAction(() => action);
    setConfirmationMessage(message);
    setShowConfirmationModal(true);
  };

  const getStatusBadge = (status: OrderStatus) => {
    const statusConfig = {
      NEW: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'New' },
      PENDING: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Pending' },
      CONFIRMED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Confirmed' },
      SHIPPED: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', label: 'Shipped' },
      DELIVERED: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Delivered' },
      CANCELLED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Cancelled' }
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'NEW': return <ShoppingBag className="w-4 h-4" />;
      case 'PENDING': return <CheckCircle className="w-4 h-4" />;
      case 'CONFIRMED': return <Check className="w-4 h-4" />;
      case 'SHIPPED': return <Truck className="w-4 h-4" />;
      case 'DELIVERED': return <CheckCircle className="w-4 h-4" />;
      case 'CANCELLED': return <X className="w-4 h-4" />;
      default: return <ShoppingBag className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="text-gray-500">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-red-500 font-medium">Error loading orders</p>
          <p className="text-gray-500 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
            <ShoppingBag className="w-8 h-8 text-indigo-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No {status.charAt(0) + status.slice(1).toLowerCase()} Orders</h3>
          <p className="text-gray-500 text-center max-w-sm">
            There are currently no {status.toLowerCase()} orders in your store.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order: Order) => (
                <tr key={order._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">#{order.orderId}</div>
                    <div className="text-sm text-gray-500">{formatDate(order.createdAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{order.shippingAddress.name}</div>
                        <div className="text-sm text-gray-500">{order.shippingAddress.mobile}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}</div>
                    <div className="text-sm text-gray-500">
                      {order.items?.reduce((total, item) => total + item.quantity, 0) || 0} total
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">{formatPrice(order.payment.amount)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/admin/orders/${order._id}`)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </button>
                      
                      {order.status === 'NEW' && (
                        <button
                          onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'PENDING'), 'Are you sure you want to verify payment for this order?')}
                          className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verify
                        </button>
                      )}
                      
                      {order.status === 'PENDING' && (
                        <button
                          onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                          className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Confirm
                        </button>
                      )}
                      
                      {order.status === 'CONFIRMED' && (
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowShippingModal(true);
                          }}
                          className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <Package className="w-3 h-3 mr-1" />
                          Ship
                        </button>
                      )}
                      
                      {order.status === 'SHIPPED' && (
                        <button
                          onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'DELIVERED'), 'Are you sure you want to mark this order as delivered?')}
                          className="inline-flex items-center justify-center w-20 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Deliver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {orders.map((order: Order) => (
          <div key={order._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900">#{order.orderId}</h3>
                <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
              </div>
              {getStatusBadge(order.status)}
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm">
                <User className="w-4 h-4 text-gray-400 mr-2" />
                <span className="text-gray-900">{order.shippingAddress.name}</span>
              </div>
              <div className="flex items-center text-sm">
                <ShoppingBag className="w-4 h-4 text-gray-400 mr-2" />
                <span className="text-gray-600">{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''} • {order.items?.reduce((total, item) => total + item.quantity, 0) || 0} total</span>
              </div>
              <div className="flex items-center text-sm">
                <IndianRupee className="w-4 h-4 text-gray-400 mr-2" />
                <span className="text-gray-900 font-medium">{formatPrice(order.payment.amount)}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-end pt-3 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate(`/admin/orders/${order._id}`)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View Details
                </button>
                
                {order.status === 'NEW' && (
                  <button
                    onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'PENDING'), 'Are you sure you want to verify payment for this order?')}
                    className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verify
                  </button>
                )}
                
                {order.status === 'PENDING' && (
                  <button
                    onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'CONFIRMED'), 'Are you sure you want to confirm this order?')}
                    className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Confirm
                  </button>
                )}
                
                {order.status === 'CONFIRMED' && (
                  <button
                    onClick={() => {
                      setSelectedOrder(order);
                      setShowShippingModal(true);
                    }}
                    className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Package className="w-3 h-3 mr-1" />
                    Ship
                  </button>
                )}
                
                {order.status === 'SHIPPED' && (
                  <button
                    onClick={() => handleConfirmAction(() => handleStatusUpdate(order._id, 'DELIVERED'), 'Are you sure you want to mark this order as delivered?')}
                    className="inline-flex items-center justify-center w-20 px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Deliver
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {showShippingModal && selectedOrder && (
        <ShippingDetailsModal
          isOpen={showShippingModal}
          orderId={selectedOrder.orderId}
          onClose={() => setShowShippingModal(false)}
          onConfirm={(shippingDetails) => {
            handleStatusUpdate(selectedOrder._id, 'SHIPPED', shippingDetails);
            setShowShippingModal(false);
          }}
        />
      )}

      {showConfirmationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Action</h3>
              <p className="text-sm text-gray-500 mb-6">{confirmationMessage}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmationAction();
                    setShowConfirmationModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList;